import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { projectsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  FolderKanban,
  Activity,
  Search,
  User,
  Mail,
  Power,
  PowerOff
} from 'lucide-react';

export default function ProjectsPage() {
  const { projects, fetchProjects, selectProject, setProjects } = useProject();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '',
    project_manager: '',
    contact_email: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      await fetchProjects();
      setLoading(false);
    };
    load();
  }, [fetchProjects]);

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
        toast.success('Project updated');
      } else {
        await projectsAPI.create(formData);
        toast.success('Project created');
      }
      await fetchProjects();
      setDialogOpen(false);
      setFormData({ 
        name: '', 
        description: '',
        project_manager: '',
        contact_email: '',
      });
      setEditingProject(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({ 
      name: project.name, 
      description: project.description || '',
      project_manager: project.project_manager || '',
      contact_email: project.contact_email || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure? This will delete all project data.')) return;
    
    try {
      await projectsAPI.delete(projectId);
      toast.success('Project deleted');
      await fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const handleToggleActive = async (project) => {
    const isActive = project.is_active !== false;
    const action = isActive ? 'deactivate' : 'activate';
    
    if (!window.confirm(`Are you sure you want to ${action} this project? ${isActive ? 'Users will not be able to access it.' : ''}`)) return;
    
    try {
      await projectsAPI.toggleActive(project.id);
      toast.success(`Project ${action}d`);
      await fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} project`);
    }
  };

  const handleOpenProject = (project) => {
    selectProject(project.id);
    navigate(`/project/${project.id}/drivers`);
  };

  const openCreateDialog = () => {
    setEditingProject(null);
    setFormData({ 
      name: '', 
      description: '',
      project_manager: '',
      contact_email: '',
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Projects
          </h1>
          <p className="text-slate-600 mt-1">Manage your stakeholder sentiment projects</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              onClick={openCreateDialog}
              data-testid="create-project-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingProject ? 'Edit Project' : 'Create New Project'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Project Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Q1 Change Initiative"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the project..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>

              {/* Project Manager & Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="project_manager" className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    Project Manager
                  </Label>
                  <Input
                    id="project_manager"
                    placeholder="e.g., John Smith"
                    value={formData.project_manager}
                    onChange={(e) => setFormData({ ...formData, project_manager: e.target.value })}
                    data-testid="project-manager-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Contact Email
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="e.g., pm@company.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                  disabled={submitting}
                  data-testid="project-submit-btn"
                >
                  {submitting ? 'Saving...' : (editingProject ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-projects-input"
        />
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array(6).fill(0).map((_, i) => (
            <Card key={i} className="dashboard-card">
              <Skeleton className="h-40" />
            </Card>
          ))}
        </div>
      ) : filteredProjects.length === 0 ? (
        <Card className="dashboard-card">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchQuery ? 'No projects found' : 'No projects yet'}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchQuery ? 'Try a different search term' : 'Create your first project to get started'}
            </p>
            {!searchQuery && (
              <Button 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={openCreateDialog}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className={`dashboard-card group cursor-pointer ${project.is_active === false ? 'opacity-60' : ''}`}
              onClick={() => handleOpenProject(project)}
              data-testid={`project-card-${project.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <img src="/logo.png" alt="Pulse" className="w-12 h-12 rounded-lg object-contain" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(project); }}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleActive(project); }}>
                        {project.is_active === false ? (
                          <>
                            <Power className="mr-2 h-4 w-4 text-green-600" />
                            <span className="text-green-600">Activate</span>
                          </>
                        ) : (
                          <>
                            <PowerOff className="mr-2 h-4 w-4 text-amber-600" />
                            <span className="text-amber-600">Deactivate</span>
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-[#5AA2AE] transition-colors">
                    {project.name}
                  </h3>
                  {project.is_active === false && (
                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                      Deactivated
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500 line-clamp-2">
                  {project.description || 'No description'}
                </p>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-400">
                    Created {new Date(project.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
